import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiFetch } from "../utils/api";

// ── Subscription state (trial countdown, autopay status, block gating) ──
const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    // Safe defaults so consumers never crash outside the provider.
    return { status: null, loading: true, blocked: false, trial: false, daysLeft: 0, refresh: () => {} };
  }
  return ctx;
};

export const SubscriptionProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch("subscription/status");
      if (res?.success && res.data) {
        setStatus(res.data);
        // Enforcement 402 also clears; keep the local flag consistent.
        if (!res.data.blocked) {
          try { localStorage.removeItem("subscription_expired"); } catch { }
        }
      }
    } catch { /* network hiccup — keep last known status */ }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = {
    status,
    loading,
    refresh,
    blocked: !!status?.blocked,
    trial: !!status?.trial,
    state: status?.state || null,
    daysLeft: status?.days_left ?? 0,
    isMaster: !!status?.is_master,
    isAdmin: !!status?.is_admin,
    hasAutopay: !!status?.has_autopay,
    cycle: status?.cycle || null,
    quantity: status?.quantity ?? null,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
